/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import {
  AddImageIcon,
  AssignmentIcon,
  CancelIcon,
  CertificateIcon,
  CreatorIcon,
  CredentialIcon,
  EditIcon,
  IBSCRenewalIcon,
  LearningPathIcon,
  LinkedServiceIcon,
  ManagerIcon,
  StudentIcon,
  SubmitIcon,
  UploadIcon,
  UploadFileIcon,
  UserIcon,
  InfoIcon,
} from 'src/v3/core/icons'

import theme from 'src/theme'
import { useFormik } from 'formik'
import * as yup from 'yup'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  ContentType,
  contentTypes,
  FileData,
} from 'src/v3/utilities/filesystem'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { DialogContentText } from '@mui/material'
import Loading from 'src/v3/components/loading'
import { fetchUser, selectUser, selectUserStatus } from './profile.slice'
import {
  selectBoundTreeStatus,
  selectLinkedServices,
  selectRoles,
  selectUserID,
} from '../settings/settings.slice'

const pages = [
  {
    name: 'Assignments',
    text: `Check the status of your current assignments and courses & see previously
    completed work.`,
    icon: <AssignmentIcon />,
    nav: 'assignments',
  },
  {
    name: 'Certificates',
    text: `See the certificates you have earned from courses in Studio.
    You can also upload external certificates.`,
    icon: <CertificateIcon />,
    nav: 'certificates',
  },
  {
    name: 'Credentials',
    text: `Upload your credentials to Studio, or update expired credentials to keep them
    up-to-date.`,
    icon: <CredentialIcon />,
    nav: 'credentials',
  },
  {
    name: 'Learning Paths',
    text: `Track your progress completing courses categorized by learning path.`,
    icon: <LearningPathIcon />,
    nav: 'learning-paths',
  },
  {
    name: 'New York Learning Paths',
    text: `Track your progress of applicable courses to the New York CME Recertification Program.`,
    icon: <LearningPathIcon />,
    nav: 'new-york-learning-paths',
  },
  {
    name: 'Linked Services',
    text: `View the linked services you are a part of and manage invites to new ones.`,
    icon: <LinkedServiceIcon />,
    nav: 'linked-services',
  },
  {
    name: 'IBSC 100-Hour',
    text: `Review your progress toward IBSC 100-hour renewal, including which courses
    still need to be completed.`,
    icon: <IBSCRenewalIcon />,
    nav: 'ibsc-100',
  },
  {
    name: 'IBSC 40-Hour',
    text: `Review your progress toward IBSC 40-hour renewal, including which courses
    still need to be completed.`,
    icon: <IBSCRenewalIcon />,
    nav: 'ibsc-40',
  },
]

let linkedServiceOnlyPages = ['Assignments', 'Linked Services']

let updateProfileImage = (data: { userID: string; imageType: string }) =>
  axios.put<string>(`/api/v2/profile/image`, data).then(pluckData)

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let userId = useAppSelector(selectUserID)
  let roles = useAppSelector(selectRoles)
  let boundTreeStatus = useAppSelector(selectBoundTreeStatus)
  let linkedServices = useAppSelector(selectLinkedServices)

  let user = useAppSelector(selectUser)
  let userStatus = useAppSelector(selectUserStatus)

  let [profileImage, setProfileImage] = useState<string>('')
  let [fileToUpload, setFileToUpload] = useState<FileData | undefined>(
    undefined,
  )
  let [existingFile, setExistingFile] = useState<string | undefined>('')
  let [open, setOpen] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let fullName = `${user?.firstName} ${user?.lastName}`

  useEffect(() => {
    if (userStatus === 'uninitialized' && userId) {
      dispatch(fetchUser())
    }
  }, [userStatus, userId])

  useEffect(() => {
    if (userId) {
      axios
        .get(`/api/v2/profile/image`)
        .then(({ data }) => data)
        .then(setProfileImage)
        .catch(() => setProfileImage(''))
    }
  }, [userId])

  let userForm = useFormik({
    validationSchema: yup.object({
      phone: yup.string(),
      email: yup.string().required(),
      address: yup.string().required(),
      city: yup.string().required(),
      state: yup.string().not(['unselected']).required(),
      zip: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      phone: '',
      email: '',
      address: '',
      city: '',
      state: '',
      zip: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      axios
        .put('/api/v2/profile/user', {
          ...user,
          id: userId,
          phone: values.phone,
          primaryEmail: values.email,
          address: values.address,
          city: values.city,
          state: values.state,
          zip: values.zip,
        })
        .then(() => dispatch(fetchUser()))
        .then(() => setOpen(''))
        .then(() => setSaving(false))
        .then(() => userForm.resetForm())
        .catch((err) => {
          setErrorMessage(err?.message || 'Error Updating User Information')
          dispatch(fetchUser())
          setOpen('')
          setSaving(false)
        })
    },
  })

  let licenseForm = useFormik({
    validationSchema: yup.object({
      licenseNumber: yup.string(),
      licenseType: yup.string().required(),
      licenseState: yup.string().not(['unselected']).required(),
      licenseExpiration: yup.date(),
    }),
    enableReinitialize: true,
    initialValues: {
      licenseNumber: '',
      licenseType: '',
      licenseState: '',
      licenseExpiration: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      axios
        .put('/api/v2/profile/user', {
          ...user,
          id: userId,
          licenseNumber: values.licenseNumber,
          licenseType: values.licenseType,
          licenseState: values.licenseState,
          licenseExpiration: values.licenseExpiration,
        })
        .then(() => dispatch(fetchUser()))
        .then(() => setOpen(''))
        .then(() => setSaving(false))
        .then(() => licenseForm.resetForm())
        .catch((err) => {
          setErrorMessage(err?.message || 'Error Updating User Information')
          dispatch(fetchUser())
          setOpen('')
          setSaving(false)
        })
    },
  })

  let nremtForm = useFormik({
    validationSchema: yup.object({
      nremtCertificationNumber: yup
        .string()
        .nullable()
        .matches(/^[a-zA-Z]/, {
          message: `NREMT number must start with a letter. Please check that you're not entering your EMS ID.`,
          excludeEmptyString: true,
        }),
      nremtCertificationDate: yup.date(),
    }),
    enableReinitialize: true,
    initialValues: {
      nremtCertificationNumber: '',
      nremtCertificationDate: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      axios
        .put('/api/v2/profile/user', {
          ...user,
          id: userId,
          nremtCertificationNumber: values.nremtCertificationNumber,
          nremtCertificationDate: values.nremtCertificationDate,
        })
        .then(() => dispatch(fetchUser()))
        .then(() => setOpen(''))
        .then(() => setSaving(false))
        .then(() => nremtForm.resetForm())
        .catch((err) => {
          setErrorMessage(err?.message || 'Error Updating User Information')
          dispatch(fetchUser())
          setOpen('')
          setSaving(false)
        })
    },
  })

  let handleCapture = (event: React.ChangeEvent) => {
    let file = (event.target as HTMLInputElement).files?.[0]
    let fileName = file?.name || ''
    let reader = new FileReader()

    reader.onloadend = () => {
      let fileData = reader.result
        ?.toString()
        ?.match(/data:[a-z]+\/[a-z]+/)?.[0]
      if (!fileData) {
        setErrorMessage('Profile Image Upload Failed: Read File Failed')
        return
      }

      let fileType = fileData.split(':')[1]
      let fileExtension = fileType.split('/')[1]

      if (!contentTypes.includes(fileType as ContentType)) {
        setErrorMessage(`Invalid File Type ${fileType}`)
        return
      }

      let binary = atob(reader.result?.toString().split(',')[1] as string)
      let array = new Array(binary.length)
        .fill('')
        .map((_, i) => binary.charCodeAt(i))
      let blobData = new Blob([new Uint8Array(array)], { type: fileExtension })

      setFileToUpload({
        name: fileName,
        type: fileType,
        extension: fileExtension,
        file: blobData,
      })
    }

    reader.readAsDataURL(file!)
  }

  let onImageEdit = () => {
    setExistingFile(profileImage)
    setOpen('image')
  }

  let onUserEdit = () => {
    userForm.setFieldValue('phone', user?.phone)
    userForm.setFieldValue('email', user?.primaryEmail)
    userForm.setFieldValue('address', user?.address)
    userForm.setFieldValue('city', user?.city)
    userForm.setFieldValue('state', user?.state)
    userForm.setFieldValue('zip', user?.zip)
    setOpen('user')
  }

  let onLicenseEdit = () => {
    licenseForm.setFieldValue('licenseNumber', user?.licenseNumber)
    licenseForm.setFieldValue('licenseType', user?.licenseType)
    licenseForm.setFieldValue('licenseState', user?.licenseState)
    licenseForm.setFieldValue('licenseExpiration', user?.licenseExpiration)
    setOpen('license')
  }

  let onNremtEdit = () => {
    nremtForm.setFieldValue(
      'nremtCertificationNumber',
      user?.nremtCertificationNumber,
    )
    nremtForm.setFieldValue(
      'nremtCertificationDate',
      user?.nremtCertificationDate,
    )
    setOpen('nremt')
  }

  return (
    <>
      <Box sx={{ textAlign: 'center', mx: 'auto' }}>
        <div style={{ marginTop: -24 }}>
          <Tooltip title="Update Profile Picture" placement="right">
            <IconButton
              sx={{ position: 'relative', top: 150, left: 75 }}
              size="small"
              onClick={onImageEdit}
            >
              <AddImageIcon />
            </IconButton>
          </Tooltip>
        </div>
        {profileImage ? (
          <Avatar
            sx={{ width: 150, height: 150, mx: 'auto' }}
            src={profileImage}
          />
        ) : (
          <Avatar
            sx={{
              width: 150,
              height: 150,
              mx: 'auto',
              fontSize: 64,
            }}
          >
            {`${user?.firstName[0]}${user?.lastName[0]}`.toUpperCase()}
          </Avatar>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            my: 0.5,
          }}
        >
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <UserIcon />
          </Avatar>
          <Typography variant="h5">{fullName}</Typography>
        </Box>
      </Box>
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          rowGap: 2,
          columnGap: 3,
          py: 2,
          px: 3,
          mb: 2,
          mt: 1,
        }}
      >
        <Box sx={{ flex: '1 1 456px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" color="text.secondary">
              Biography
            </Typography>
            <IconButton size="small" onClick={onUserEdit}>
              <EditIcon />
            </IconButton>
          </Box>
          <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 20 }}>
            <div>
              <strong>Name:</strong> {fullName || 'Unknown'}
            </div>
            <div>
              <strong>Phone:</strong> {user?.phone || 'Unknown'}
            </div>
            <div>
              <strong>Email:</strong> {user?.primaryEmail || 'Unknown'}
            </div>
            <div>
              <strong>Address:</strong>{' '}
              {user && user.address && user.city && user.state && user.zip ? (
                <span>
                  {user.address} {user?.city}, {user?.state} {user?.zip}
                </span>
              ) : (
                <span>Unknown</span>
              )}
            </div>
          </div>
        </Box>
        <Box sx={{ flex: '1 1 304px' }}>
          <Typography variant="subtitle1" color="text.secondary">
            Roles
          </Typography>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10,
              marginTop: 2,
              marginBottom: 4,
            }}
          >
            {[
              { name: 'Manager', icon: <ManagerIcon /> },
              { name: 'Creator', icon: <CreatorIcon /> },
              { name: 'Student', icon: <StudentIcon /> },
            ].map((role) => (
              <Chip
                key={role.name}
                label={role.name}
                color="secondary"
                variant={
                  roles?.includes(role.name.toLowerCase())
                    ? 'filled'
                    : 'outlined'
                }
                icon={role.icon}
              />
            ))}
          </div>
        </Box>
        <Box sx={{ flex: '1 1 456px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" color="text.secondary">
              Primary License
            </Typography>
            <IconButton size="small" onClick={onLicenseEdit}>
              <EditIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              sm: {
                flexDirection: 'column',
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: 20,
              },
              md: { flexDirection: 'row' },
            }}
          >
            <div>
              <strong>Number:</strong> {user?.licenseNumber || 'Unknown'}
            </div>
            <div>
              <strong>Type:</strong> {user?.licenseType || 'Unknown'}
            </div>
            <div>
              <strong>State:</strong> {user?.licenseState || 'Unknown'}
            </div>
            <div>
              <strong>Expiration:</strong>{' '}
              {user?.licenseExpiration
                ? dayjs(user?.licenseExpiration).format('MM/DD/YYYY')
                : 'Unknown'}
            </div>
            {boundTreeStatus !== 'limited' && (
              <div
                className="cursor-pointer"
                onClick={() => navigate('/profile/credentials')}
              >
                <strong>Additional Licenses</strong>{' '}
                <Tooltip title="Additional licenses can be added on the Credentials page.">
                  <InfoIcon />
                </Tooltip>
              </div>
            )}
          </Box>
        </Box>
        <Box sx={{ flex: '1 1 304px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" color="text.secondary">
              NREMT Certification
            </Typography>
            <IconButton size="small" onClick={onNremtEdit}>
              <EditIcon />
            </IconButton>
          </Box>
          <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 20 }}>
            <div>
              <strong>Number:</strong>{' '}
              {user?.nremtCertificationNumber || 'Unknown'}
            </div>
            <div>
              <strong>Date:</strong>{' '}
              {user?.nremtCertificationDate
                ? dayjs(user?.nremtCertificationDate).format('MM/DD/YYYY')
                : 'Unknown'}
            </div>
          </div>
        </Box>
      </Paper>
      <Divider sx={{ mb: 2 }} />
      {boundTreeStatus !== 'limited' && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: 'repeat(auto-fit, minmax(252px, 1fr))',
          }}
        >
          {pages.map(
            (page) =>
              ((linkedServiceOnlyPages.includes(page.name) &&
                linkedServices.length > 0) ||
                !linkedServiceOnlyPages.includes(page.name)) && (
                <Box
                  key={page.name}
                  sx={{
                    pt: 1,
                    height: '100%',
                    border: `1px solid ${theme.palette.primary.dark}`,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 1,
                      px: 2,
                    }}
                  >
                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                      {page.icon}
                    </Avatar>
                    <Typography variant="h5">{page.name}</Typography>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ my: 1, px: 2, flexGrow: 1 }}
                  >
                    {page.text}
                  </Typography>
                  <Box
                    sx={{
                      borderBottom: `1px solid ${theme.palette.primary.dark}`,
                    }}
                  ></Box>
                  <Button
                    fullWidth
                    sx={{
                      borderRadius: '0 0 8px 8px',
                      '&:hover': {
                        backgroundColor: theme.palette.background.paper,
                      },
                    }}
                    onClick={() => navigate(`/profile/${page.nav}`)}
                  >
                    View {page.name}
                  </Button>
                </Box>
              ),
          )}
        </Box>
      )}
      <Dialog
        open={open === 'image' && !saving}
        onClose={() => {
          setOpen('')
          setFileToUpload(undefined)
        }}
      >
        <Paper sx={{ width: 300, px: 2, py: 1 }}>
          <DialogTitle sx={{ textAlign: 'center', mb: -1.5 }}>
            Edit Profile Image
          </DialogTitle>
          <DialogContent
            sx={{
              px: 2,
              pb: 2,
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <Box>
              <label
                style={{
                  marginTop: 0,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                >
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    type="file"
                    onChange={(e) => handleCapture(e)}
                  />
                  {!existingFile ? 'Select' : 'Overwrite'} File
                </Button>
              </label>
              {fileToUpload && (
                <Typography variant="subtitle1">
                  <UploadFileIcon
                    color="primary"
                    sx={{ mr: '2px', mb: '2px' }}
                  />
                  {fileToUpload.name}
                </Typography>
              )}
              {existingFile && (
                <Typography color="text.secondary" variant="subtitle2">
                  *The existing profile image will only be overwritten upon
                  submitting this form with a new image uploaded.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions className="flex justify-center pt-0 pb-3 mt-0">
            {saving ? (
              <CircularProgress className="m-auto" size={36} />
            ) : (
              <>
                <Button
                  color="primary"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<SubmitIcon />}
                  disabled={!existingFile && !fileToUpload}
                  onClick={() => {
                    setSaving(true)
                    updateProfileImage({
                      userID: userId!,
                      imageType:
                        fileToUpload?.extension === 'jpeg' ? 'jpg' : 'png',
                    })
                      .then((response) =>
                        axios.put(response, fileToUpload?.file, {
                          headers: {
                            'Cache-Control': 'max-age=2592000',
                            'content-type': fileToUpload?.type,
                          },
                        }),
                      )
                      .then(() =>
                        axios
                          .get('/api/v2/profile/image')
                          .then(({ data }) => data)
                          .then(setProfileImage)
                          .catch(() => setProfileImage('')),
                      )
                      .then(() => {
                        setSaving(false)
                        setOpen('')
                      })
                      .catch((err) => {
                        setSaving(false)
                        setOpen('')
                        setErrorMessage(
                          err?.message || 'Profile Image Upload Failed',
                        )
                      })
                  }}
                >
                  Submit
                </Button>
                <Button
                  onClick={() => {
                    setOpen('')
                    setFileToUpload(undefined)
                  }}
                  color="error"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
              </>
            )}
          </DialogActions>
        </Paper>
      </Dialog>
      <Dialog open={open === 'user' && !saving} onClose={() => setOpen('')}>
        <form onSubmit={userForm.handleSubmit}>
          <Paper sx={{ width: 300, px: 2, py: 1 }}>
            <DialogTitle sx={{ textAlign: 'center', mb: -1.5 }}>
              Edit User Information
            </DialogTitle>
            <DialogContent
              sx={{
                px: 2,
                pb: 1,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.phone}
                label="Phone"
                name="phone"
                onChange={userForm.handleChange}
                error={userForm.touched.phone && !!userForm.errors.phone}
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.email}
                label="Email"
                name="email"
                // onChange={userForm.handleChange}
                // error={userForm.touched.email && !!userForm.errors.email}
                disabled
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.address}
                label="Address"
                name="address"
                onChange={userForm.handleChange}
                error={userForm.touched.address && !!userForm.errors.address}
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.city}
                label="City"
                name="city"
                onChange={userForm.handleChange}
                error={userForm.touched.city && !!userForm.errors.city}
              />
              <FormControl fullWidth>
                <InputLabel sx={{ ml: -2 }}>State</InputLabel>
                <Select
                  variant="standard"
                  value={userForm.values.state}
                  label="State"
                  name="state"
                  onChange={userForm.handleChange}
                  error={userForm.touched.state && !!userForm.errors.state}
                >
                  <MenuItem key="unselected" value="unselected">
                    Unselected
                  </MenuItem>
                  <MenuItem value="Alaska">Alaska</MenuItem>
                  <MenuItem value="Alabama">Alabama</MenuItem>
                  <MenuItem value="Antigua and Barbuda">
                    Antigua and Barbuda
                  </MenuItem>
                  <MenuItem value="Arkansas">Arkansas</MenuItem>
                  <MenuItem value="Australia">Australia</MenuItem>
                  <MenuItem value="Arizona">Arizona</MenuItem>
                  <MenuItem value="The Bahamas">The Bahamas</MenuItem>
                  <MenuItem value="Belgium">Belgium</MenuItem>
                  <MenuItem value="California">California</MenuItem>
                  <MenuItem value="Canada">Canada</MenuItem>
                  <MenuItem value="Colorado">Colorado</MenuItem>
                  <MenuItem value="Connecticut">Connecticut</MenuItem>
                  <MenuItem value="Czech Republic">Czech Republic</MenuItem>
                  <MenuItem value="Delaware">Delaware</MenuItem>
                  <MenuItem value="DHS">DHS</MenuItem>
                  <MenuItem value="District of Columbia">
                    District of Columbia
                  </MenuItem>
                  <MenuItem value="Ecuador">Ecuador</MenuItem>
                  <MenuItem value="Florida">Florida</MenuItem>
                  <MenuItem value="France">France</MenuItem>
                  <MenuItem value="Georgia">Georgia</MenuItem>
                  <MenuItem value="Germany">Germany</MenuItem>
                  <MenuItem value="Great Britain">Great Britain</MenuItem>
                  <MenuItem value="Guam">Guam</MenuItem>
                  <MenuItem value="Hawaii">Hawaii</MenuItem>
                  <MenuItem value="Iowa">Iowa</MenuItem>
                  <MenuItem value="Idaho">Idaho</MenuItem>
                  <MenuItem value="Illinois">Illinois</MenuItem>
                  <MenuItem value="Indiana">Indiana</MenuItem>
                  <MenuItem value="Iraq">Iraq</MenuItem>
                  <MenuItem value="Ireland">Ireland</MenuItem>
                  <MenuItem value="Kansas">Kansas</MenuItem>
                  <MenuItem value="Kentucky">Kentucky</MenuItem>
                  <MenuItem value="Louisiana">Louisiana</MenuItem>
                  <MenuItem value="Liechtenstein">Liechtenstein</MenuItem>
                  <MenuItem value="Massachusetts">Massachusetts</MenuItem>
                  <MenuItem value="Maryland">Maryland</MenuItem>
                  <MenuItem value="Maine">Maine</MenuItem>
                  <MenuItem value="Mexico">Mexico</MenuItem>
                  <MenuItem value="Michigan">Michigan</MenuItem>
                  <MenuItem value="Minnesota">Minnesota</MenuItem>
                  <MenuItem value="Missouri">Missouri</MenuItem>
                  <MenuItem value="Mississippi">Mississippi</MenuItem>
                  <MenuItem value="Montana">Montana</MenuItem>
                  <MenuItem value="North Carolina">North Carolina</MenuItem>
                  <MenuItem value="North Dakota">North Dakota</MenuItem>
                  <MenuItem value="Nebraska">Nebraska</MenuItem>
                  <MenuItem value="Netherlands">Netherlands</MenuItem>
                  <MenuItem value="New Zealand">New Zealand</MenuItem>
                  <MenuItem value="New Hampshire">New Hampshire</MenuItem>
                  <MenuItem value="New Jersey">New Jersey</MenuItem>
                  <MenuItem value="New Mexico">New Mexico</MenuItem>
                  <MenuItem value="Nevada">Nevada</MenuItem>
                  <MenuItem value="New York">New York</MenuItem>
                  <MenuItem value="Ohio">Ohio</MenuItem>
                  <MenuItem value="Oklahoma">Oklahoma</MenuItem>
                  <MenuItem value="Oregon">Oregon</MenuItem>
                  <MenuItem value="Pennsylvania">Pennsylvania</MenuItem>
                  <MenuItem value="Puerto Rico">Puerto Rico</MenuItem>
                  <MenuItem value="Poland">Poland</MenuItem>
                  <MenuItem value="Qatar">Qatar</MenuItem>
                  <MenuItem value="Rhode Island">Rhode Island</MenuItem>
                  <MenuItem value="Saudi Arabia">Saudi Arabia</MenuItem>
                  <MenuItem value="Serbia">Serbia</MenuItem>
                  <MenuItem value="South Africa">South Africa</MenuItem>
                  <MenuItem value="South Carolina">South Carolina</MenuItem>
                  <MenuItem value="Scotland">Scotland</MenuItem>
                  <MenuItem value="South Dakota">South Dakota</MenuItem>
                  <MenuItem value="Slovakia">Slovakia</MenuItem>
                  <MenuItem value="Switzerland">Switzerland</MenuItem>
                  <MenuItem value="Tennessee">Tennessee</MenuItem>
                  <MenuItem value="Texas">Texas</MenuItem>
                  <MenuItem value="Trinidad and Tobago">
                    Trinidad and Tobago
                  </MenuItem>
                  <MenuItem value="UAE">UAE</MenuItem>
                  <MenuItem value="Utah">Utah</MenuItem>
                  <MenuItem value="Virgin Islands">Virgin Islands</MenuItem>
                  <MenuItem value="Virginia">Virginia</MenuItem>
                  <MenuItem value="Vermont">Vermont</MenuItem>
                  <MenuItem value="Washington">Washington</MenuItem>
                  <MenuItem value="Wisconsin">Wisconsin</MenuItem>
                  <MenuItem value="West Virginia">West Virginia</MenuItem>
                  <MenuItem value="Wyoming">Wyoming</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.zip}
                label="ZIP"
                name="zip"
                onChange={userForm.handleChange}
                error={userForm.touched.zip && !!userForm.errors.zip}
              />
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingBottom: 3,
              }}
            >
              <Button
                type="submit"
                color="primary"
                variant="contained"
                sx={{ flex: '1 1 0px' }}
                startIcon={<SubmitIcon />}
              >
                Submit
              </Button>
              <Button
                onClick={() => setOpen('')}
                color="error"
                variant="contained"
                sx={{ flex: '1 1 0px' }}
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
            </DialogActions>
          </Paper>
        </form>
      </Dialog>
      <Dialog open={open === 'license' && !saving} onClose={() => setOpen('')}>
        <form onSubmit={licenseForm.handleSubmit}>
          <Paper sx={{ width: 300, px: 2, py: 1 }}>
            <DialogTitle sx={{ textAlign: 'center', mb: -1.5 }}>
              Edit Primary License Information
            </DialogTitle>
            <DialogContentText>
              When you specify a primary license, it will show up as the primary
              license on your PDF certificate of continuing educutation upon
              completion of a course. If the license state is one we collaborate
              with, your CE will be automatically reported to the state.
            </DialogContentText>
            <DialogContent
              sx={{
                px: 2,
                pb: 1,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                value={licenseForm.values.licenseNumber}
                label="License Number"
                name="licenseNumber"
                onChange={licenseForm.handleChange}
                error={
                  licenseForm.touched.licenseNumber &&
                  !!licenseForm.errors.licenseNumber
                }
              />
              <FormControl fullWidth>
                <InputLabel sx={{ ml: -2 }}>License Type</InputLabel>
                <Select
                  variant="standard"
                  value={licenseForm.values.licenseType}
                  label="License Type"
                  name="licenseType"
                  onChange={licenseForm.handleChange}
                  error={
                    licenseForm.touched.licenseType &&
                    !!licenseForm.errors.licenseType
                  }
                >
                  <MenuItem key="unselected" value="unselected">
                    Unselected
                  </MenuItem>
                  <MenuItem value="" />
                  <MenuItem value="Paramedic">Paramedic</MenuItem>
                  <MenuItem value="Registered Nurse">Registered Nurse</MenuItem>
                  <MenuItem value="EMR">Emergency Medical Responder</MenuItem>
                  <MenuItem value="CFR">Certified First Responder</MenuItem>
                  <MenuItem value="EMT">EMT</MenuItem>
                  <MenuItem value="EMT-1">EMT-1</MenuItem>
                  <MenuItem value="EMT-B">EMT-B</MenuItem>
                  <MenuItem value="EMT-D">EMT-D</MenuItem>
                  <MenuItem value="AEMT">AEMT</MenuItem>
                  <MenuItem value="EMT-2">EMT-2</MenuItem>
                  <MenuItem value="EMT-CC">EMT-CC</MenuItem>
                  <MenuItem value="Advanced Care Paramedic (Canada Only)">
                    Advanced Care Paramedic (Canada Only)
                  </MenuItem>
                  <MenuItem value="Primary Care Paramedic (Canada Only)">
                    Primary Care Paramedic (Canada Only)
                  </MenuItem>
                  <MenuItem value="Physician">Physician</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel sx={{ ml: -2 }}>License State</InputLabel>
                <Select
                  variant="standard"
                  value={licenseForm.values.licenseState}
                  label="License State"
                  name="licenseState"
                  onChange={licenseForm.handleChange}
                  error={
                    licenseForm.touched.licenseState &&
                    !!licenseForm.errors.licenseState
                  }
                >
                  <MenuItem key="unselected" value="unselected">
                    Unselected
                  </MenuItem>
                  <MenuItem value="Alaska">Alaska</MenuItem>
                  <MenuItem value="Alabama">Alabama</MenuItem>
                  <MenuItem value="Antigua and Barbuda">
                    Antigua and Barbuda
                  </MenuItem>
                  <MenuItem value="Arkansas">Arkansas</MenuItem>
                  <MenuItem value="Australia">Australia</MenuItem>
                  <MenuItem value="Arizona">Arizona</MenuItem>
                  <MenuItem value="The Bahamas">The Bahamas</MenuItem>
                  <MenuItem value="Belgium">Belgium</MenuItem>
                  <MenuItem value="California">California</MenuItem>
                  <MenuItem value="Canada">Canada</MenuItem>
                  <MenuItem value="Colorado">Colorado</MenuItem>
                  <MenuItem value="Connecticut">Connecticut</MenuItem>
                  <MenuItem value="Czech Republic">Czech Republic</MenuItem>
                  <MenuItem value="Delaware">Delaware</MenuItem>
                  <MenuItem value="DHS">DHS</MenuItem>
                  <MenuItem value="District of Columbia">
                    District of Columbia
                  </MenuItem>
                  <MenuItem value="Ecuador">Ecuador</MenuItem>
                  <MenuItem value="Florida">Florida</MenuItem>
                  <MenuItem value="France">France</MenuItem>
                  <MenuItem value="Georgia">Georgia</MenuItem>
                  <MenuItem value="Germany">Germany</MenuItem>
                  <MenuItem value="Great Britain">Great Britain</MenuItem>
                  <MenuItem value="Guam">Guam</MenuItem>
                  <MenuItem value="Hawaii">Hawaii</MenuItem>
                  <MenuItem value="Iowa">Iowa</MenuItem>
                  <MenuItem value="Idaho">Idaho</MenuItem>
                  <MenuItem value="Illinois">Illinois</MenuItem>
                  <MenuItem value="Indiana">Indiana</MenuItem>
                  <MenuItem value="Iraq">Iraq</MenuItem>
                  <MenuItem value="Ireland">Ireland</MenuItem>
                  <MenuItem value="Kansas">Kansas</MenuItem>
                  <MenuItem value="Kentucky">Kentucky</MenuItem>
                  <MenuItem value="Louisiana">Louisiana</MenuItem>
                  <MenuItem value="Liechtenstein">Liechtenstein</MenuItem>
                  <MenuItem value="Massachusetts">Massachusetts</MenuItem>
                  <MenuItem value="Maryland">Maryland</MenuItem>
                  <MenuItem value="Maine">Maine</MenuItem>
                  <MenuItem value="Mexico">Mexico</MenuItem>
                  <MenuItem value="Michigan">Michigan</MenuItem>
                  <MenuItem value="Minnesota">Minnesota</MenuItem>
                  <MenuItem value="Missouri">Missouri</MenuItem>
                  <MenuItem value="Mississippi">Mississippi</MenuItem>
                  <MenuItem value="Montana">Montana</MenuItem>
                  <MenuItem value="North Carolina">North Carolina</MenuItem>
                  <MenuItem value="North Dakota">North Dakota</MenuItem>
                  <MenuItem value="Nebraska">Nebraska</MenuItem>
                  <MenuItem value="Netherlands">Netherlands</MenuItem>
                  <MenuItem value="New Zealand">New Zealand</MenuItem>
                  <MenuItem value="New Hampshire">New Hampshire</MenuItem>
                  <MenuItem value="New Jersey">New Jersey</MenuItem>
                  <MenuItem value="New Mexico">New Mexico</MenuItem>
                  <MenuItem value="Nevada">Nevada</MenuItem>
                  <MenuItem value="New York">New York</MenuItem>
                  <MenuItem value="Ohio">Ohio</MenuItem>
                  <MenuItem value="Oklahoma">Oklahoma</MenuItem>
                  <MenuItem value="Oregon">Oregon</MenuItem>
                  <MenuItem value="Pennsylvania">Pennsylvania</MenuItem>
                  <MenuItem value="Puerto Rico">Puerto Rico</MenuItem>
                  <MenuItem value="Poland">Poland</MenuItem>
                  <MenuItem value="Qatar">Qatar</MenuItem>
                  <MenuItem value="Rhode Island">Rhode Island</MenuItem>
                  <MenuItem value="Saudi Arabia">Saudi Arabia</MenuItem>
                  <MenuItem value="Serbia">Serbia</MenuItem>
                  <MenuItem value="South Africa">South Africa</MenuItem>
                  <MenuItem value="South Carolina">South Carolina</MenuItem>
                  <MenuItem value="Scotland">Scotland</MenuItem>
                  <MenuItem value="South Dakota">South Dakota</MenuItem>
                  <MenuItem value="Slovakia">Slovakia</MenuItem>
                  <MenuItem value="Switzerland">Switzerland</MenuItem>
                  <MenuItem value="Tennessee">Tennessee</MenuItem>
                  <MenuItem value="Texas">Texas</MenuItem>
                  <MenuItem value="Trinidad and Tobago">
                    Trinidad and Tobago
                  </MenuItem>
                  <MenuItem value="UAE">UAE</MenuItem>
                  <MenuItem value="Utah">Utah</MenuItem>
                  <MenuItem value="Virgin Islands">Virgin Islands</MenuItem>
                  <MenuItem value="Virginia">Virginia</MenuItem>
                  <MenuItem value="Vermont">Vermont</MenuItem>
                  <MenuItem value="Washington">Washington</MenuItem>
                  <MenuItem value="Wisconsin">Wisconsin</MenuItem>
                  <MenuItem value="West Virginia">West Virginia</MenuItem>
                  <MenuItem value="Wyoming">Wyoming</MenuItem>
                </Select>
              </FormControl>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="License Expiration Date"
                  value={dayjs(licenseForm.values.licenseExpiration)}
                  onChange={(dayJsVal: any) =>
                    licenseForm.handleChange({
                      target: {
                        name: 'licenseExpiration',
                        value: dayjs(dayJsVal).format('MM/DD/YYYY'),
                      },
                    })
                  }
                />
              </LocalizationProvider>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingBottom: 3,
              }}
            >
              <Button
                type="submit"
                color="primary"
                variant="contained"
                sx={{ flex: '1 1 0px' }}
                startIcon={<SubmitIcon />}
              >
                Submit
              </Button>
              <Button
                onClick={() => setOpen('')}
                color="error"
                variant="contained"
                sx={{ flex: '1 1 0px' }}
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
            </DialogActions>
          </Paper>
        </form>
      </Dialog>
      <Dialog open={open === 'nremt' && !saving} onClose={() => setOpen('')}>
        <form onSubmit={nremtForm.handleSubmit}>
          <Paper sx={{ width: 300, px: 2, py: 1 }}>
            <DialogTitle sx={{ textAlign: 'center', mb: -1.5 }}>
              Edit NREMT Information
            </DialogTitle>
            <DialogContent
              sx={{
                px: 2,
                pb: 1,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                value={nremtForm.values.nremtCertificationNumber}
                label="NREMT Certification Number"
                name="nremtCertificationNumber"
                onChange={nremtForm.handleChange}
                error={!!nremtForm.errors?.nremtCertificationNumber}
                helperText={nremtForm.errors?.nremtCertificationNumber}
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="NREMT Expiration Date"
                  value={dayjs(nremtForm?.values?.nremtCertificationDate)}
                  onChange={(dayJsVal: any) =>
                    nremtForm.handleChange({
                      target: {
                        name: 'nremtCertificationDate',
                        value: dayjs(dayJsVal).format('MM/DD/YYYY'),
                      },
                    })
                  }
                />
              </LocalizationProvider>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingBottom: 3,
              }}
            >
              <Button
                type="submit"
                color="primary"
                variant="contained"
                sx={{ flex: '1 1 0px' }}
                startIcon={<SubmitIcon />}
              >
                Submit
              </Button>
              <Button
                onClick={() => setOpen('')}
                color="error"
                variant="contained"
                sx={{ flex: '1 1 0px' }}
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
            </DialogActions>
          </Paper>
        </form>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading entities={[userStatus]} loading={saving} />
    </>
  )
}
